import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import ImageContainer from '@components/ImageContainer';
import { sendWarning } from '@components/Noti';
import WidgetRowAlign from '@components/PlayerWidgets/WidgetRowAlign';
import SortableList from '@components/SortableList';
import TextLink from '@components/TextLink';
import Textarea from '@components/Textarea';
import Tip from '@components/Tip';
import { Validator } from '@components/Validator';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import type { CFMedia, CFMediaImage } from '@types';
import arrayMove from '@utils/arrayMove';
/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import type { SetOptional } from 'type-fest';
import { selectLibImages } from '../../../mediaLib';
import { useCustomFeature } from '../../CustomFeatureContext';
import GalleryItem from './GalleryItem';

const MAX = 100;

type CFMediaFixed = SetOptional<CFMedia, 'imgs'>;

function CGallerySetting() {
  const { data, setData } = useCustomFeature<CFMediaFixed>();
  const { imgs = [] } = data;
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    if (imgs.length) {
      setSelected(0);
    }
  }, []);

  const textHandlers = useTextareaHandelrs('trimRight', (val) => {
    if (selected === null) return;
    setData((draft) => {
      draft.imgs ??= [];
      draft.imgs[selected].content = val;
    });
  });

  const handleAlignChange = (val: 'left' | 'center' | 'right') => {
    if (selected === null) return;
    setData((draft) => {
      draft.imgs ??= [];
      draft.imgs[selected].contentAlign = val;
    });
  };
  const handleSelectImages = async () => {
    if (imgs.length >= MAX) {
      sendWarning(`最多支持添加${MAX}张图片`);
      return;
    }
    const { libItems } = await selectLibImages({ multiple: true });
    if (!libItems) return;
    if (libItems.length + imgs.length > MAX) {
      sendWarning('图片数超过上限，部分素材未添加成功');
      libItems.length = MAX - imgs.length;
    }
    const newItems: CFMediaImage[] = libItems.map((x) => ({
      mediaId: x.id,
      mediaUrl: x.url,
      content: '',
      contentAlign: 'center',
    }));
    setData((draft) => {
      draft.imgs ??= [];
      draft.imgs = [...draft.imgs, ...newItems];
    });
    if (selected === null) {
      setSelected(0);
    }
  };

  const handleDelete = (index: number) => {
    if (index === selected) {
      setSelected(null);
    } else if (selected && index < selected) {
      setSelected((prev) => prev! - 1);
    }
    setData((draft) => {
      draft.imgs ??= [];
      draft.imgs.splice(index, 1);
    });
  };

  const handleApplyTo = () => {
    setData((draft) => {
      draft.imgs ??= [];
      draft.imgs = draft.imgs.map((x) => ({
        ...x,
        content: imgs[selected!].content,
        contentAlign: imgs[selected!].contentAlign,
      }));
    });
  };

  const renderList = () => {
    return (
      <div className="h-full w-full">
        <Scrollbars>
          <SortableList
            axis="xy"
            distance={10}
            onSortEnd={(sort) => {
              setData((draft) => {
                draft.imgs ??= [];
                draft.imgs = arrayMove({
                  array: draft.imgs,
                  fromIndex: sort.oldIndex,
                  toIndex: sort.newIndex,
                });
              });
            }}
            onSortStart={() => {
              setSelected(null);
            }}
          >
            <div className="flex flex-wrap items-center">
              {imgs.map((x, i) => (
                <GalleryItem
                  key={i}
                  index={i}
                  listIndex={i}
                  src={x.mediaUrl}
                  mediaId={x.mediaId}
                  active={selected === i}
                  withText={!!x.content}
                  onClick={setSelected}
                  onDeleteClick={handleDelete}
                />
              ))}
            </div>
          </SortableList>
        </Scrollbars>
      </div>
    );
  };
  return (
    <Collapse title="图文设置">
      <div className="flex">
        <div>图册：</div>
        <div className="text-black-gray">（拖动可调整图片顺序）</div>
      </div>
      <Validator
        value={imgs}
        validFn={(value) => !!value.length}
        warningMessage="请选择图片"
      >
        {({ warning }) => (
          <ImageContainer
            warning={warning}
            className="mt-10 h-155"
            placeholder={
              <div className="flex flex-col items-center">
                <p>单个图片长/宽不超过2000PX</p>
                <p>{`图片总数不超过${MAX}张`}</p>
              </div>
            }
          >
            {!!imgs.length && renderList()}
          </ImageContainer>
        )}
      </Validator>
      <div className="mt-10 flex items-center justify-between">
        <Tip>单图片不超过700KB</Tip>
        <Button size="sm" onClick={handleSelectImages}>
          选择图片
        </Button>
      </div>
      {selected !== null && (
        <>
          <div className="mt-20 flex justify-between">
            <div>配图文字：</div>
            <TextLink onClick={handleApplyTo}>应用到所有</TextLink>
          </div>
          <Textarea
            className="mt-10 h-115 w-full"
            scheme="dark"
            placeholder="为图片添加文字描述"
            maxLength={5000}
            value={imgs[selected].content}
            {...textHandlers}
          />
          <WidgetRowAlign
            hasJustify
            value={imgs[selected].contentAlign ?? 'center'}
            onChange={handleAlignChange}
          />
        </>
      )}
    </Collapse>
  );
}

export default CGallerySetting;
